/**
 * Color utility functions for HSV/RGB/Hex conversions and canvas coordinate mapping
 */

export interface HSV {
  h: number; // 0-360
  s: number; // 0-1
  v: number; // 0-1
}

export interface RGB {
  r: number; // 0-255
  g: number; // 0-255
  b: number; // 0-255
}

export interface Point {
  x: number;
  y: number;
}

/**
 * Convert HSV to RGB
 */
export function hsvToRgb(h: number, s: number, v: number): RGB {
  // Normalize hue to 0-360 range, treating 360 as 0
  h = h % 360;
  if (h < 0) h += 360;

  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;

  let r = 0,
    g = 0,
    b = 0;

  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

/**
 * Convert RGB to HSV
 */
export function rgbToHsv(r: number, g: number, b: number): HSV {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;

  let h = 0;
  const s = max === 0 ? 0 : diff / max;
  const v = max;

  if (diff !== 0) {
    switch (max) {
      case r:
        h = ((g - b) / diff) % 6;
        break;
      case g:
        h = (b - r) / diff + 2;
        break;
      case b:
        h = (r - g) / diff + 4;
        break;
    }
  }

  h = Math.round(h * 60);
  if (h < 0) h += 360;

  return { h, s, v };
}

/**
 * Convert RGB to hex string
 */
export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => {
    const hex = Math.round(Math.max(0, Math.min(255, n))).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/**
 * Convert hex string to RGB
 */
export function hexToRgb(hex: string): RGB | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result || result.length < 4) {
    return null;
  }

  return {
    r: parseInt(result[1]!, 16),
    g: parseInt(result[2]!, 16),
    b: parseInt(result[3]!, 16),
  };
}

/**
 * Convert hex to HSV
 */
export function hexToHsv(hex: string): HSV | null {
  const rgb = hexToRgb(hex);
  return rgb ? rgbToHsv(rgb.r, rgb.g, rgb.b) : null;
}

/**
 * Convert HSV to hex
 */
export function hsvToHex(h: number, s: number, v: number): string {
  const rgb = hsvToRgb(h, s, v);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}

/**
 * Convert canvas coordinates to HSV saturation and value
 */
export function canvasToHsv(x: number, y: number, canvasWidth: number, canvasHeight: number): { s: number; v: number } {
  const s = Math.max(0, Math.min(1, x / canvasWidth));
  const v = Math.max(0, Math.min(1, 1 - y / canvasHeight));
  return { s, v };
}

/**
 * Convert HSV saturation and value to canvas coordinates
 */
export function hsvToCanvas(s: number, v: number, canvasWidth: number, canvasHeight: number): Point {
  const x = s * canvasWidth;
  const y = (1 - v) * canvasHeight;
  return { x, y };
}

/**
 * Convert slider position to hue value
 */
export function sliderToHue(x: number, sliderWidth: number): number {
  return Math.max(0, Math.min(360, (x / sliderWidth) * 360));
}

/**
 * Convert hue value to slider position
 */
export function hueToSlider(hue: number, sliderWidth: number): number {
  return (hue / 360) * sliderWidth;
}

/**
 * Validate hex color string
 */
export function isValidHex(hex: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(hex);
}

/**
 * Ensure hex string has proper format
 */
export function normalizeHex(hex: string): string {
  if (!hex.startsWith("#")) {
    hex = "#" + hex;
  }
  return hex.toUpperCase();
}
